import { OrgRole, isOrgRoleSuperset } from "@hex/common";
import { useMemo } from "react";

import { useUserForMagic } from "../../../../hooks/magicHooks";
import { useCurrentUser } from "../../../../hooks/me/useCurrentUser";
import { isSupportUser } from "../../../../util/supportUser";
import { useFeatureGates } from "../../../feature-gate/FeatureGateContext";

import { useGetThreadsSettings } from "./useGetThreadsSettings";

// returns true of threads is accessible to the current user
export function useThreadsEnabledForUser(): {
  canCreateNewThread: boolean;
  canViewThreadById: boolean;
  loading: boolean;
  minimumRoleForAccess: OrgRole | undefined;
  disabledForRole: boolean;
  hasFeatureGate: boolean;
} {
  const currentUser = useCurrentUser();
  const { loading: magicLoading, magicEnabled } = useUserForMagic();

  // 'Ask' uses the exploreFromScratch feature gate to determine if the user can create Explores.
  const { exploreFromScratch } = useFeatureGates();

  const {
    loading: settingsLoading,
    minimumRoleForAccess,
    settings,
  } = useGetThreadsSettings();

  return useMemo(() => {
    if (settingsLoading || magicLoading)
      return {
        canCreateNewThread: false,
        canViewThreadById: false,
        loading: true,
        hasFeatureGate: exploreFromScratch,
        minimumRoleForAccess,
        disabledForRole: true,
      };

    const canCreateThreadsPerSettingsPanel =
      settings != null && settings.enabledForRole;

    // This just unblocks the support user
    // Whether "Shared with support" is toggled on - that's handled by AskThreadPermissionsCheck/safeAgentChatThreadById
    const isSupport = isSupportUser(currentUser);

    const hasMemberOrgRole =
      !!currentUser?.orgRole &&
      isOrgRoleSuperset(currentUser.orgRole, OrgRole.MEMBER);

    const baseViewRequirements =
      isSupport || (magicEnabled && hasMemberOrgRole);

    const createEnabledForRole =
      canCreateThreadsPerSettingsPanel &&
      !!currentUser?.orgRole &&
      isOrgRoleSuperset(currentUser.orgRole, OrgRole.EXPLORER);

    // Only if the user is an explorer+ and the settings panel is enabled.
    const canCreateNewThread =
      baseViewRequirements && exploreFromScratch && createEnabledForRole;

    return {
      canCreateNewThread,
      canViewThreadById: baseViewRequirements,
      loading: false,
      hasFeatureGate: exploreFromScratch,
      minimumRoleForAccess,
      disabledForRole: !createEnabledForRole,
    };
  }, [
    currentUser,
    exploreFromScratch,
    magicEnabled,
    magicLoading,
    settings,
    settingsLoading,
    minimumRoleForAccess,
  ]);
}
